import { useState, useEffect } from "react";

const useAccordion = ({
  mode = "closable", // closable | always-one | max-one
  expandedItems = [], // always-one requires one initial item
  onToggle = () => {},
}) => {
  if (!["closable", "always-one", "max-one"].includes(mode)) {
    throw new Error(`Unrecognized "${mode}" for Accordion`);
  }
  if (mode === "always-one" && expandedItems.length !== 1) {
    throw new Error("always-one mode requires only one initial item");
  }

  if (mode === "max-one" && expandedItems.length > 1) {
    throw new Error("max-one mode allows none or only one initial item");
  }

  const [expanded, setExpanded] = useState(expandedItems);
  const isItemExpanded = (item) => expanded?.includes(item);
  const isItemToggleable = (item) =>
    mode === "always-one" ? !isItemExpanded(item) : true;

  useEffect(() => {
    setExpanded(expandedItems);
  }, [expandedItems]);

  const onItemToggle = (item, event) => {
    switch (mode) {
      case "always-one": {
        if (!isItemExpanded(item)) {
          setExpanded([item]);
          onToggle({ item, expanded: true }, event);
        }
        break;
      }

      case "max-one": {
        if (isItemExpanded(item)) {
          setExpanded([]);
          onToggle({ item, expanded: false }, event);
        } else {
          setExpanded([item]);
          onToggle({ item, expanded: true }, event);
        }
        break;
      }

      case "closable":
      default: {
        if (isItemExpanded(item)) {
          setExpanded(expanded.filter((expandedItem) => item !== expandedItem));
          onToggle({ item, expanded: false }, event);
        } else {
          setExpanded([...expanded, item]);
          onToggle({ item, expanded: true }, event);
        }
      }
    }
  };

  return {
    isItemToggleable,
    isItemExpanded,
    onItemToggle,
  };
};

export default useAccordion;
